import React, { useState } from "react";

import { tokenize } from "./McpHighlight";

export const Chevron = ({ open }) => (
  <svg
    className={`om-mcp-chevron ${open ? "is-open" : ""}`}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="#528BFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const iconProps = {
  viewBox: "0 0 24 24",
  width: 22,
  height: 22,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

export const UserIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
  </svg>
);

export const ChatIcon = () => (
  <svg {...iconProps}>
    <path d="M4 5h16v11H9l-5 4z" />
    <path d="M8 9h8M8 12h5" />
  </svg>
);

export const ServerIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="18" height="6" rx="2" />
    <rect x="3" y="14" width="18" height="6" rx="2" />
    <circle cx="7" cy="7" r=".7" />
    <circle cx="7" cy="17" r=".7" />
  </svg>
);

export const PinIcon = () => (
  <svg {...iconProps}>
    <path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

export const CopyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect
      x="7.5"
      y="7.5"
      width="10"
      height="10"
      rx="2"
      stroke="#F9FAFB"
      strokeWidth="1.25"
    />
    <path
      d="M13.5 5.5v-1a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h1"
      stroke="#F9FAFB"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
  </svg>
);

export const TickIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 10.5L8 14.5L16 6"
      stroke="#7DDCA4"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const writeToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const helper = document.createElement("textarea");

  helper.value = text;
  helper.setAttribute("readonly", "");
  helper.style.position = "absolute";
  helper.style.left = "-9999px";

  document.body.appendChild(helper);
  helper.select();
  document.execCommand("copy");
  document.body.removeChild(helper);
};

const linesToText = (lines) =>
  lines
    .map((line) =>
      typeof line === "string" ? line : line.map((part) => part.t).join("")
    )
    .join("\n");


export const CodeBlock = ({ code, lines, mode = "plain", label }) => {
  const [copied, setCopied] = useState(false);

  const rendered = lines || tokenize(code, mode);
  const text = lines ? linesToText(lines) : code;

  const handleCopy = async () => {
    try {
      await writeToClipboard(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <div className="om-mcp-code">
      <button
        type="button"
        className="om-mcp-copy"
        onClick={handleCopy}
        aria-label={copied ? "Copied" : `Copy ${label || "code"}`}
      >
        {copied ? <TickIcon /> : <CopyIcon />}
      </button>
      <pre>
        <code>
          {rendered.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {typeof line === "string"
                ? line
                : line.map((part, partIndex) => (
                    <span
                      key={partIndex}
                      className={part.tone ? `om-mcp-t-${part.tone}` : undefined}
                    >
                      {part.t}
                    </span>
                  ))}
              {lineIndex < rendered.length - 1 ? "\n" : ""}
            </React.Fragment>
          ))}
        </code>
      </pre>
      <span className="om-mcp-sr" role="status" aria-live="polite">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </div>
  );
};

/** A white card with a small label above a code block. */
export const CodeCard = ({ title, code, lines, mode, label, note }) => (
  <div className="om-mcp-code-card">
    <div className="om-mcp-code-card-title">{title}</div>
    <div className="om-mcp-code-card-body">
      <CodeBlock code={code} lines={lines} mode={mode} label={label || title} />
    </div>
    {note && <p className="om-mcp-code-card-note">{note}</p>}
  </div>
);

/** Grey label followed by a horizontal rule. */
export const SectionRule = ({ children, id }) => (
  <div className="om-mcp-rule" id={id}>
    <span>{children}</span>
  </div>
);

/** Grey inset panel used for "Before publishing". */
export const InsetNote = ({ title, children }) => (
  <div className="om-mcp-note">
    <h4 className="om-mcp-note-title">{title}</h4>
    <p className="om-mcp-card-body-16">{children}</p>
  </div>
);


/**
 * Inline rich text from data.
 * Accepts a plain string, or an array of parts:
 *   { t: "text", v }   plain text (include your own spaces)
 *   { t: "em", v }     italic
 *   { t: "strong", v } bold
 *   { t: "chip", v }   grey inline chip, e.g. a parameter name
 *   { t: "mark", v }   highlighted token (om-mcp-mark)
 */
export const RichText = ({ parts }) => {
  if (!parts) return null;
  if (typeof parts === "string") return parts;

  return parts.map((part, index) => {
    switch (part.t) {
      case "em":
        return <em key={index}>{part.v}</em>;
      case "strong":
        return <strong key={index}>{part.v}</strong>;
      case "chip":
        return (
          <code key={index} className="om-mcp-chip">
            {part.v}
          </code>
        );
      case "mark":
        return (
          <mark key={index} className="om-mcp-mark">
            {part.v}
          </mark>
        );
      default:
        return <React.Fragment key={index}>{part.v}</React.Fragment>;
    }
  });
};

export const ParamParts = ({ parts = [] }) => (
  <>
    {parts.map((part, index) =>
      part.t === "chip" ? (
        <code key={index} className="om-mcp-chip">
          {part.v}
        </code>
      ) : part.t === "mark" ? (
        <mark key={index} className="om-mcp-mark">
          {part.v}
        </mark>
      ) : (
        <span key={index} className="om-mcp-param-text">
          {part.v}
        </span>
      )
    )}
  </>
);

export const ParamRow = ({ name, required, parts }) => (
  <li className="om-mcp-param">
    <code className="om-mcp-param-name">{name}</code>
    <span className={required ? "om-mcp-req" : "om-mcp-opt"}>
      {required ? "required" : "optional"}
    </span>
    <ParamParts parts={parts} />
  </li>
);

export const ToolCard = ({ tool }) => (
  <article
    className={`om-mcp-card om-mcp-tool ${
      tool.wide ? "om-mcp-tool-wide" : ""
    }`}
  >
    <div className="om-mcp-tool-head">
      <code className="om-mcp-tool-name">{tool.name}</code>
      <span className={`om-mcp-badge om-mcp-badge-${tool.categoryKey}`}>
        {tool.category}
      </span>
    </div>

    <p className="om-mcp-tool-desc">
      <RichText parts={tool.description} />
    </p>

    <h4 className="om-mcp-params-label">Parameters</h4>
    <ul className="om-mcp-params">
      {tool.params.map((param) => (
        <ParamRow key={param.name} {...param} />
      ))}
    </ul>

    <div className="om-mcp-example-label">Example arguments</div>
    <CodeBlock
      code={tool.example}
      mode="json"
      label={`${tool.name} example arguments`}
    />
  </article>
);

export const Troubleshooting = ({ rows }) => (
  <article className="om-mcp-card om-mcp-trouble">
    {rows.map((row) => (
      <div className="om-mcp-trouble-row" key={row.label}>
        <div className="om-mcp-trouble-label">
          {row.chip ? (
            <span className="om-mcp-chip om-mcp-chip-status">{row.label}</span>
          ) : (
            <span className="om-mcp-trouble-plain">{row.label}</span>
          )}
        </div>
        <p className="om-mcp-card-body">
          <RichText parts={row.cause} />
        </p>
        <p className="om-mcp-card-body">
          <RichText parts={row.fix} />
        </p>
      </div>
    ))}
  </article>
);

export const QuickReference = ({ rows }) => (
  <div className="om-mcp-qref">
    <div className="om-mcp-qref-head">
      <div>Tool</div>
      <div>Category</div>
      <div>Required params</div>
      <div>Best used for</div>
    </div>
    {rows.map((row) => (
      <div className="om-mcp-qref-row" key={row.name}>
        <div className="om-mcp-qref-name">{row.name}</div>
        <div>
          <span className={`om-mcp-badge om-mcp-badge-${row.categoryKey}`}>
            {row.category}
          </span>
        </div>
        <div className="om-mcp-qref-params">
          {row.required.length === 0 ? (
            <span className="om-mcp-param-text">none</span>
          ) : (
            <ParamParts parts={row.required} />
          )}
        </div>
        <div className="om-mcp-card-body">
          <RichText parts={row.use} />
        </div>
      </div>
    ))}
  </div>
);